// --------------------------------------------------------------
// Created On: 2024-09-27
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useMemo, useState } from "react";
import { API } from "../../constants/miscellaneous";
import useReduxSelector from "src/hooks/useReduxSelector";
import useApi from "../../hooks/useApi";
import apiRequest from "../../utilities/api/apiRequest";
import getApiError from "../../utilities/api/getApiError";
import Spinner from "../../components/Spinner/Spinner";
import Card from "../../components/Card/Card";
import ColorPicker from "../../components/ColorPicker/ColorPicker";
import Error from "../../components/Error/Error";
import Success from "../../components/Success/Success";

// Page for setting company wide preferences, such as default paint color.
export default function CompanyPreferencePage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [paintColors, setPaintColors] = useState<PaintColor[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [selectedPaintColorId, setSelectedPaintColorId] = useState<number | null>(null);
  const [previousPaintColorId, setPreviousPaintColorId] = useState<number | null>(null);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const selectedPaintColor = useMemo(
    () => getPaintColor(paintColors, selectedPaintColorId),
    [JSON.stringify(paintColors), selectedPaintColorId]
  );

  // Clear error/success messages whenever a setting is changed.
  useEffect(() => {
    setErrorMessage("");
    setSuccessMessage("");
  }, [selectedPaintColorId]);

  // Get company preferences.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      url: `${API}/company/${currentUser.companyId}/preference`,
      method: "GET",
    },
    async (response: Response, responseBody: CompanyPreferenceResponseBody) => {
      if (response.ok && responseBody && responseBody.defaultPaintColor !== null) {
        setErrorMessage("");
        setSelectedPaintColorId(responseBody.defaultPaintColor.paintColorId);
        setPreviousPaintColorId(responseBody.defaultPaintColor.paintColorId);
      } else if (!response.ok) {
        setErrorMessage(await getApiError(response, "Unable to retrieve company preference setting."));
      } else {
        setSelectedPaintColorId(null);
      }
      setLoading(false);
    },
    []
  );

  // Retrieve paint colors from database.
  useApi(
    () => {
      return true;
    },
    {
      url: `${API}/paint-color`,
      method: "GET",
    },
    async (response: Response, responseBody: PaintColorResponseBody) => {
      if (response.ok && responseBody) {
        setPaintColors(responseBody.paintColors);
      } else {
        setErrorMessage(await getApiError(response, "Error loading paint colors."));
      }
    },
    []
  );

  // Get the selected paint color.
  function getPaintColor(paintColors: PaintColor[], selectedPaintColorId: number | null): PaintColor | null {
    const paintColorIndex = paintColors.findIndex((paintColor) => paintColor.paintColorId === selectedPaintColorId);
    if (paintColorIndex > -1) {
      return paintColors[paintColorIndex];
    } else {
      return null;
    }
  }

  // Update company preferences.
  async function saveChanges(paintColorId: number | null | undefined): Promise<void> {
    setLoading(true);
    const requestBody = {
      defaultPaintColor: paintColorId,
    };
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/preference`,
      "PUT",
      requestBody
    )) as [Response, UpdatedPreferenceResponseBody];
    if (response.ok && responseBody && paintColorId !== undefined) {
      setErrorMessage("");
      setSelectedPaintColorId(paintColorId);
      setPreviousPaintColorId(paintColorId);
      setSuccessMessage("Company preference settings have been updated.");
    } else {
      setSuccessMessage("");
      setErrorMessage(await getApiError(response, "Unable to update company preference setting."));
    }
    setLoading(false);
  }

  // Reverts all changes to the last time preferences were saved.
  function undoChanges(): void {
    setSelectedPaintColorId(previousPaintColorId);
  }

  return (
    <div className="col-lg-5 col-12 pt-5 mx-auto">
      <Spinner loading={loading} />
      <Card title="Company Preferences">
        {!loading && (
          <Fragment>
            <h6 className="mx-3 pt-3 pb-2">Default Paint Color for Parts</h6>
            <div className="mx-4 pb-5">
              <ColorPicker
                colors={paintColors}
                selectedColorId={selectedPaintColor ? selectedPaintColor.paintColorId : null}
                onChange={(selectedPaintColorId) => setSelectedPaintColorId(selectedPaintColorId)}
              />

              {(errorMessage.length > 0 || successMessage.length > 0) && (
                <div className="status-messages mt-3">
                  <Error message={errorMessage} />
                  <Success message={successMessage} />
                </div>
              )}

              {/* Hide save / undo button if an success or error message is present. */}
              {errorMessage.length === 0 && successMessage.length === 0 && (
                <div>
                  <button
                    className={`btn btn-primary mx-2 float-end`}
                    type="button"
                    onClick={() => saveChanges(selectedPaintColorId)}
                  >
                    Save Changes
                  </button>
                  <button className={`btn btn-secondary mx-2 float-end`} type="button" onClick={() => undoChanges()}>
                    Undo Changes
                  </button>
                </div>
              )}
            </div>
          </Fragment>
        )}
      </Card>
    </div>
  );
}

interface PaintColorResponseBody {
  paintColors: PaintColor[];
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface CompanyPreferenceResponseBody {
  defaultPaintColor: PaintColor | null;
}

interface UpdatedPreferenceResponseBody {
  defaultPaintColor: number;
}
